import type { ChartDataItem } from '../types';

const TOTAL_LINE_TOOLTIP_GAP = 2;

export function isNegativeStackSeriesAtPoint(
  field: string,
  dataPoint: ChartDataItem,
  chartData: ChartDataItem[],
): boolean {
  const value = dataPoint[field];
  if (typeof value !== 'number') return false;
  if (value !== 0) return value < 0;
  return chartData.some((point) => typeof point[field] === 'number' && point[field] < 0);
}

// Return the selected account band's top edge, or the total line's own value. Unlisted fields
// retain ECharts' existing legend-tooltip positioning.
export function getStackedBandAnchorY(
  field: string,
  dataPoint: ChartDataItem,
  chartData: ChartDataItem[],
  chartFields: string[],
  stackedAreaLegendTooltipFields: string[],
  totalField?: string,
): number | null {
  if (!stackedAreaLegendTooltipFields.includes(field)) return null;
  if (field === totalField) {
    const value = dataPoint[field];
    return typeof value === 'number' ? value : null;
  }

  let positiveTotal = 0;
  let negativeTotal = 0;

  for (const currentField of chartFields) {
    const rawValue = dataPoint[currentField];
    const value = typeof rawValue === 'number' ? rawValue : 0;
    const negative = isNegativeStackSeriesAtPoint(currentField, dataPoint, chartData);

    if (currentField === field) {
      return negative ? negativeTotal : positiveTotal + value;
    }

    if (negative) negativeTotal += value;
    else positiveTotal += value;
  }

  return null;
}

export const buildLegendTooltipPosition =
  (getPlacement: () => 'above' | 'aboveWithGap' | null) =>
  (
    point: [number, number],
    _params: unknown,
    _dom: unknown,
    _rect: unknown,
    size: { contentSize: [number, number]; viewSize: [number, number] },
  ): [number, number] => {
    const [contentWidth, contentHeight] = size.contentSize;
    const [viewWidth, viewHeight] = size.viewSize;
    const placement = getPlacement();
    const preferredX = placement
      ? point[0] - contentWidth
      : point[0] + contentWidth + 22 > viewWidth
        ? point[0] - contentWidth - 20
        : point[0] + 20;
    const x = Math.max(0, Math.min(preferredX, viewWidth - contentWidth));
    const preferredY = placement
      ? point[1] - contentHeight - (placement === 'aboveWithGap' ? TOTAL_LINE_TOOLTIP_GAP : 0)
      : point[1] + contentHeight + 20 > viewHeight
        ? point[1] - contentHeight - 20
        : point[1] + 20;
    const y = Math.max(0, Math.min(preferredY, viewHeight - contentHeight));

    return [x, y];
  };
